
import React from 'react';
import { SlideData } from './types';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import ReachCalculator from './components/ReachCalculator';

export const SLIDES: SlideData[] = [
  {
    id: 1,
    title: "Clubfluence",
    subtitle: "Turning club communities into measurable media channels\n\nUnlocking local, trusted reach — and monetizing it",
    type: 'title',
    speakerNotes: "We help clubs monetize something they already have but can’t currently sell.",
    accentColor: "#8b5cf6"
  },
  {
    id: 2,
    title: "Clubs sell logos. Sponsors buy reach.",
    points: [
      "Local sponsorship budgets still exist",
      "Ad spend has moved to digital and social channels",
      "Sponsors measure ROI through reach and impressions"
    ],
    type: 'problem',
    extraContent: (
      <div className="mt-8 p-12 bg-red-900/20 border-2 border-red-500/30 rounded-3xl">
        <p className="text-red-400 font-black mb-4 uppercase text-xl tracking-[0.2em]">Key Problem</p>
        <p className="text-5xl leading-tight font-bold">Clubs still sell visibility the old way — while sponsors buy reach digitally.</p>
      </div>
    ),
    speakerNotes: "This is not a club execution problem. It’s a market mismatch."
  },
  {
    id: 3,
    title: "Sponsorship value has shifted from spectators to members",
    points: [
      "Traditional sponsorship rewarded TV time and match-day audiences",
      "Many clubs have neither — despite large, active memberships",
      "Digital reach makes membership itself commercially valuable"
    ],
    type: 'problem',
    extraContent: (
      <div className="mt-8 flex flex-col gap-8">
        <div className="p-12 bg-purple-900/20 border-2 border-purple-500/30 rounded-3xl">
          <p className="text-purple-400 font-black mb-4 uppercase text-xl tracking-[0.2em]">Key Takeaway</p>
          <p className="text-5xl leading-tight font-bold text-white">Entire categories of clubs become sponsor-relevant for the first time.</p>
        </div>
        <p className="text-gray-500 text-xl font-medium tracking-wide">
          Gymnastics · Martial arts · Dance · Youth academies · Training-focused sports
        </p>
      </div>
    ),
    speakerNotes: "Digital reach makes membership itself commercially valuable."
  },
  {
    id: 4,
    title: "Club-owned channels are structurally disadvantaged",
    points: [
      "Social platforms prioritize individual accounts",
      "Club accounts reach only ~5–10% of followers",
      "Posting more does not materially increase reach",
      "Members are the true distribution layer"
    ],
    type: 'problem',
    extraContent: (
      <div className="mt-8 flex flex-col gap-10">
        <div className="flex items-center justify-between p-12 bg-gray-900/50 rounded-[40px] border border-white/10 shadow-[0_40px_100px_rgba(0,0,0,0.5)]">
          <div className="text-center">
            <p className="text-2xl text-gray-400 mb-2 italic">Club post (Instagram)</p>
            <p className="text-8xl font-black text-red-500">160</p>
            <p className="text-sm text-gray-500 uppercase tracking-widest mt-2">reach from 2,000 followers</p>
          </div>
          <div className="text-5xl text-gray-700 font-black">VS</div>
          <div className="text-center">
            <p className="text-2xl text-gray-400 mb-2 italic">Community amplification</p>
            <p className="text-8xl font-black text-emerald-500">42,000</p>
            <p className="text-sm text-gray-500 uppercase tracking-widest mt-2">collective reach</p>
          </div>
        </div>
        
        <div className="p-8 bg-white/5 border-2 border-white/10 rounded-[30px] relative">
          <p className="text-sm text-gray-500 font-mono uppercase tracking-[0.4em] mb-6 border-b border-white/5 pb-4">THE MECHANISM (PLATFORM-DRIVEN)</p>
          <div className="flex justify-between items-center text-center px-4">
            <div>
              <p className="text-4xl font-black text-white">500</p>
              <p className="text-xs text-gray-500 uppercase tracking-widest mt-1">Members</p>
            </div>
            <div className="text-3xl text-gray-700">×</div>
            <div>
              <p className="text-4xl font-black text-white">70%</p>
              <p className="text-xs text-gray-500 uppercase tracking-widest mt-1">participation (low-effort share)</p>
            </div>
            <div className="text-3xl text-gray-700">×</div>
            <div>
              <p className="text-4xl font-black text-white">400</p>
              <p className="text-xs text-gray-500 uppercase tracking-widest mt-1">Avg Followers</p>
            </div>
            <div className="text-3xl text-gray-700">×</div>
            <div>
              <p className="text-4xl font-black text-emerald-500">30%</p>
              <p className="text-xs text-gray-500 uppercase tracking-widest mt-1">visibility</p>
            </div>
            <div className="text-3xl text-gray-700 font-black">=</div>
            <div>
              <p className="text-5xl font-black text-emerald-400">42k</p>
              <p className="text-xs text-gray-500 uppercase tracking-widest mt-1">Total</p>
            </div>
          </div>
          <p className="absolute -bottom-10 right-0 text-xs text-gray-600 italic">
            Assumes opt-in participation and conservative visibility rates.
          </p>
        </div>
      </div>
    ),
    speakerNotes: "The club handle is effectively invisible because platforms prioritize individuals. This is a structural rule, not an execution failure."
  },
  {
    id: 5,
    title: "The hidden asset",
    subtitle: "Clubs control hundreds of personal audiences — but have no way to activate them together",
    points: [
      "Reach lives across hundreds of private, trusted accounts",
      "Each account performs well — but only individually",
      "Clubs lack infrastructure to coordinate this reach"
    ],
    type: 'problem',
    extraContent: (
      <div className="flex flex-col gap-10">
        <div>
          <p className="text-sm text-gray-500 uppercase tracking-[0.4em] mb-6">Independent community audiences</p>
          <div className="flex flex-wrap gap-6 max-w-2xl">
            {['Parents', 'Athletes', 'Alumni', 'Volunteers', 'Fans'].map(tag => (
              <span key={tag} className="px-8 py-4 bg-purple-900/30 border-2 border-purple-500/40 rounded-full text-purple-200 text-2xl font-bold">
                {tag}
              </span>
            ))}
          </div>
        </div>

        <div className="p-10 bg-red-900/10 border-l-8 border-red-500/50 rounded-r-3xl">
           <p className="text-4xl font-black text-white leading-tight">
             Result: Real media value exists — but is economically inaccessible.
           </p>
        </div>
      </div>
    ),
    speakerNotes: "We assemble the fragments into a high-value media channel. The value is there, it's just fragmented and impossible to sell as one inventory block without Clubfluence."
  },
  {
    id: 6,
    title: "Club-powered content engine",
    subtitle: "High-impact content created by a few — distributed by many",
    points: [
      "Small number of members create authentic UGC (unboxing, tasting, challenges, reactions)",
      "Content performs natively on creator platforms (e.g. TikTok)",
      "Wider membership amplifies content through low-effort sharing"
    ],
    type: 'solution',
    extraContent: (
      <div className="flex flex-col gap-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="p-10 bg-purple-900/20 border-2 border-purple-500/30 rounded-[40px] shadow-xl">
            <p className="text-purple-400 font-black text-2xl mb-6 uppercase tracking-widest">High-Impact Creation</p>
            <p className="text-3xl font-bold text-white leading-tight">
              15–20% of members generate native, high-performing content.
            </p>
          </div>
          <div className="p-10 bg-emerald-900/20 border-2 border-emerald-500/30 rounded-[40px] shadow-xl">
            <p className="text-emerald-400 font-black text-2xl mb-6 uppercase tracking-widest">Guaranteed Distribution</p>
            <p className="text-3xl font-bold text-white leading-tight">
              The wider community ensures predictable reach beyond the algorithm.
            </p>
          </div>
        </div>
        
        <div className="p-12 bg-white/5 border-2 border-white/10 rounded-[40px] text-center italic">
          <p className="text-5xl font-black text-white leading-tight">
            "Creation drives attention. <span className="text-purple-500">Amplification guarantees reach.</span>"
          </p>
        </div>
      </div>
    ),
    speakerNotes: "We don't need everyone to be a creator. We leverage the 20% who are already native to these platforms and use the 80% to fuel the distribution."
  },
  {
    id: 7,
    title: "Why it works",
    subtitle: "Incentivized participation built into operations",
    points: [
      "Sharing is part of team identity",
      "Platform drives deadlined action",
      "Incentives align goals"
    ],
    type: 'solution',
    extraContent: (
      <div className="mt-12 flex flex-col gap-12">
        <div className="flex gap-20">
           <div className="text-center">
              <p className="text-9xl font-black text-purple-500">~70%</p>
              <p className="text-xl text-gray-500 uppercase tracking-[0.3em] font-bold mt-4">Share rate</p>
           </div>
           <div className="text-center">
              <p className="text-9xl font-black text-purple-500">~20%</p>
              <p className="text-xl text-gray-500 uppercase tracking-[0.3em] font-bold mt-4">Creators</p>
           </div>
        </div>
      </div>
    ),
    speakerNotes: "70% of team members share. This isn't optional social media behavior; it's a team operation."
  },
  {
    id: 8,
    title: "The Economic Engine",
    subtitle: "Converting attention into sponsor-ready inventory",
    type: 'data',
    extraContent: (
      <div className="mt-8 flex flex-col gap-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          {/* Reach Section */}
          <div className="bg-gray-900/50 border-2 border-white/5 rounded-[40px] p-12">
            <p className="text-lg font-black text-gray-500 uppercase tracking-[0.4em] mb-10">Attention Uplift</p>
            <div className="space-y-10">
              <div className="flex justify-between items-end">
                <span className="text-xl text-gray-400">Club Handle (2k Followers)</span>
                <span className="text-4xl font-black text-red-500">160</span>
              </div>
              <div className="h-4 bg-white/5 rounded-full overflow-hidden">
                <div className="h-full bg-red-500" style={{ width: '0.5%' }}></div>
              </div>
              <div className="flex justify-between items-end">
                <span className="text-xl text-gray-200 font-bold tracking-tight">The Network (500 Members)</span>
                <span className="text-4xl font-black text-emerald-500">68,000</span>
              </div>
              <div className="h-4 bg-white/5 rounded-full overflow-hidden shadow-[0_0_20px_rgba(16,185,129,0.3)]">
                <div className="h-full bg-emerald-500" style={{ width: '100%' }}></div>
              </div>
              <p className="text-2xl text-emerald-500 font-black italic text-right">425x Reach Increase</p>
            </div>
          </div>

          {/* Value Section */}
          <div className="bg-emerald-950/20 border-2 border-emerald-500/30 rounded-[40px] p-12 relative overflow-hidden">
            <p className="text-lg font-black text-emerald-500 uppercase tracking-[0.4em] mb-10">Media Value</p>
            <div className="flex items-center gap-12 mb-10">
               <div>
                  <p className="text-xl text-gray-400 uppercase tracking-widest font-bold">Baseline CPM</p>
                  <p className="text-6xl font-black">€20</p>
               </div>
               <div className="h-20 w-px bg-white/10" />
               <div>
                  <p className="text-xl text-gray-400 uppercase tracking-widest font-bold">Total Reach</p>
                  <p className="text-6xl font-black">68k</p>
               </div>
            </div>
            <div className="pt-10 border-t-2 border-emerald-500/20">
               <p className="text-2xl text-emerald-400 font-black mb-2 uppercase">Value / Campaign</p>
               <p className="text-9xl font-black text-white tracking-tighter">€1,360</p>
            </div>
          </div>
        </div>

        <div className="p-8 bg-purple-900/10 border-2 border-purple-500/20 rounded-[30px] flex items-center gap-10">
          <p className="text-2xl text-purple-200 leading-tight">
            <strong>The Math:</strong> 500 members × 70% share × 400 followers × 30% reach = <strong>42,000</strong> reach. Add parent layers & active creators for a <strong>68,000</strong> campaign total.
          </p>
        </div>
      </div>
    ),
    speakerNotes: "This is new money. We've turned a invisible post into a €1,360 asset."
  },
  {
    id: 9,
    title: "Club business case",
    subtitle: "Clubs earn real money — predictably (500-member club)",
    type: 'model',
    extraContent: (
      <div className="mt-8 overflow-hidden rounded-[40px] border-2 border-white/10">
        <table className="w-full text-left bg-gray-900/40">
          <thead>
            <tr className="bg-white/5">
              <th className="p-10 text-gray-400 text-xl font-bold uppercase tracking-widest">Revenue Source</th>
              <th className="p-10 text-gray-400 text-xl font-bold uppercase tracking-widest text-right">Annual</th>
            </tr>
          </thead>
          <tbody className="divide-y-2 divide-white/5 text-4xl">
            <tr>
              <td className="p-10 text-gray-300">Like & Share Campaigns</td>
              <td className="p-10 text-right font-black text-white">€3,600</td>
            </tr>
            <tr>
              <td className="p-10 text-gray-300">Content Campaigns</td>
              <td className="p-10 text-right font-black text-white">€9,000</td>
            </tr>
            <tr>
              <td className="p-10 text-gray-300">Local Sponsor Ads</td>
              <td className="p-10 text-right font-black text-white">€2,000</td>
            </tr>
            <tr className="bg-purple-900/30">
              <td className="p-10 font-black text-purple-300">Total Club Income</td>
              <td className="p-10 text-right font-black text-purple-300 text-6xl">~€14,600</td>
            </tr>
          </tbody>
        </table>
        <div className="p-6 text-center text-xl text-gray-500 font-bold uppercase tracking-[0.2em] bg-black/40">No staff. No buying. No risk.</div>
      </div>
    ),
    speakerNotes: "For a local club, €15k is a transformative amount of annual revenue."
  },
  {
    id: 10,
    title: "When clubs earn, we earn",
    subtitle: "Perfectly aligned software-as-a-service model",
    points: [
      "Platform fee: 20–30%",
      "No inventory risk",
      "Pure software margins"
    ],
    type: 'model',
    extraContent: (
      <div className="mt-8 p-16 bg-gradient-to-br from-purple-900/40 to-black rounded-[60px] border-2 border-purple-500/20 shadow-2xl">
        <p className="text-2xl text-gray-400 mb-6 font-bold uppercase tracking-widest">Per-club ARR Target:</p>
        <div className="flex justify-between items-end">
          <div>
            <p className="text-[10rem] font-black text-white leading-none tracking-tighter">€4k<span className="text-4xl text-purple-500 ml-4 font-bold tracking-widest uppercase">ARR</span></p>
          </div>
          <div className="text-right pb-4">
             <div className="h-32 w-32 bg-purple-500 rounded-[40px] flex items-center justify-center text-black font-black text-5xl shadow-[0_20px_60px_rgba(139,92,246,0.4)]">
               25%
             </div>
             <p className="text-sm text-gray-500 uppercase font-black tracking-[0.4em] mt-6">Avg Take Rate</p>
          </div>
        </div>
      </div>
    ),
    speakerNotes: "When the club makes €15k, we make roughly €4k. Pure software scale."
  },
  {
    id: 11,
    title: "Scale: Marketplace economics",
    type: 'data',
    extraContent: (
      <div className="mt-6 h-[600px] flex flex-col items-center">
        <ResponsiveContainer width="100%" height="80%">
          <BarChart
            data={[
              { name: '100 Clubs', val: 0.4 },
              { name: '300 Clubs', val: 1.2 },
              { name: '1000 Clubs', val: 4.0 }
            ]}
            margin={{ top: 20, right: 40, left: 40, bottom: 80 }}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#334155" strokeWidth={2} />
            <XAxis 
              dataKey="name" 
              stroke="#94a3b8" 
              fontSize={24} 
              tickMargin={25} 
              fontWeight="bold" 
              axisLine={false}
              tickLine={false}
            />
            <YAxis 
              tickFormatter={(val) => `€${val}M`} 
              stroke="#94a3b8" 
              fontSize={20} 
              tickMargin={15}
              axisLine={false}
              tickLine={false}
              width={100}
            />
            <Tooltip 
              contentStyle={{ backgroundColor: '#0f172a', border: '2px solid #334155', borderRadius: '20px', padding: '20px' }}
              itemStyle={{ fontSize: '24px', fontWeight: 'bold' }}
              cursor={{ fill: 'rgba(255,255,255,0.05)' }}
            />
            <Bar dataKey="val" fill="#8b5cf6" radius={[15, 15, 0, 0]} barSize={140} />
          </BarChart>
        </ResponsiveContainer>
        <p className="text-center text-gray-500 text-2xl mt-4 font-black uppercase tracking-[0.5em]">The Path to €4M+ ARR</p>
      </div>
    ),
    speakerNotes: "1,000 clubs is the first milestone. That's a massive network reaching millions. The labels are now clear and the scale is undeniable."
  },
  {
    id: 12,
    title: "Why this wins",
    subtitle: "Why now — and why us",
    points: [
      "Social reach is the new currency",
      "Sponsors want authenticity",
      "Clubs are the last trusted hubs"
    ],
    type: 'solution',
    extraContent: (
      <div className="mt-12 p-16 bg-white/5 rounded-[50px] border-2 border-white/10 text-5xl font-black leading-tight italic text-gray-300 shadow-2xl">
        "We turn clubs into media channels without changing who they are."
      </div>
    ),
    speakerNotes: "We are building on existing community trust."
  },
  {
    id: 13,
    title: "Unlocking Value",
    subtitle: "The future of community-driven media",
    type: 'closing',
    extraContent: (
      <div className="mt-12 text-center">
        <div className="inline-block p-20 bg-purple-600 rounded-[80px] shadow-[0_40px_100px_rgba(139,92,246,0.3)] mb-12 border-4 border-white/20">
           <p className="text-white text-9xl font-black mb-8 uppercase tracking-tighter">Clubfluence</p>
           <p className="text-purple-100 max-w-2xl text-4xl font-light leading-snug">We don't create attention.<br/>We unlock it — and take a share when it turns into revenue.</p>
        </div>
        <p className="text-gray-500 uppercase tracking-[0.8em] text-xl font-black mt-12">Invest in the ecosystem</p>
      </div>
    ),
    speakerNotes: "The value is there. The attention is there. We just provide the bridge."
  },
  {
    id: 14,
    title: "Impact Simulator",
    subtitle: "Visualize the structural reach advantage",
    type: 'model',
    extraContent: <ReachCalculator />,
    speakerNotes: "Use this interactive tool to demonstrate the 'X Factor'. By sliding the active amplifiers, you can see how the multiplier creates immediate earned media value for even a small club."
  }
];
